import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Phone, Mail, Star, Award, BadgeCheck, Clock, Users, Zap } from 'lucide-react';
import type { TrustBadgeProps } from '../types';

const iconMap: Record<string, React.ComponentType<{ className?: string; style?: React.CSSProperties }>> = {
  identity: BadgeCheck,
  contact: Phone,
  email: Mail,
  transaction: Star,
  social: Users,
  premium: Award,
  verified: Shield,
  speed: Clock,
  power: Zap,
};

const tierColors = {
  bronze: {
    bg: 'bg-amber-100',
    text: 'text-amber-700',
    border: 'border-amber-200',
    gradient: 'from-amber-400 to-orange-500',
  },
  silver: {
    bg: 'bg-slate-100',
    text: 'text-slate-700',
    border: 'border-slate-200',
    gradient: 'from-slate-300 to-slate-500',
  },
  gold: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-700',
    border: 'border-yellow-200',
    gradient: 'from-yellow-400 to-amber-500',
  },
  platinum: {
    bg: 'bg-purple-100',
    text: 'text-purple-700',
    border: 'border-purple-200',
    gradient: 'from-purple-400 to-pink-500',
  },
};

const sizeConfig = {
  sm: { icon: 14, padding: 'p-1.5', text: 'text-xs' },
  md: { icon: 18, padding: 'p-2', text: 'text-sm' },
  lg: { icon: 24, padding: 'p-3', text: 'text-base' },
};

export function TrustBadgeComponent({
  badge,
  size = 'md',
  animateUnlock = false,
  onClick,
}: TrustBadgeProps): JSX.Element {
  const Icon = iconMap[badge.icon] || Star;
  const colors = tierColors[badge.tier];
  const config = sizeConfig[size];
  const isEarned = badge.earnedAt !== null;
  const progress = badge.progress;

  return (
    <AnimatePresence>
      <motion.div
        className={`relative group ${onClick ? 'cursor-pointer' : ''}`}
        onClick={onClick}
        initial={animateUnlock ? { scale: 0, rotate: -180 } : false}
        animate={{ scale: 1, rotate: 0 }}
        whileHover={{ scale: 1.05, y: -2 }}
        transition={{ type: 'spring', stiffness: 200, damping: 15 }}
      >
        {/* Badge container */}
        <div
          className={`
            relative rounded-xl border-2 transition-all duration-300
            ${config.padding}
            ${isEarned ? colors.bg : 'bg-gray-50'}
            ${isEarned ? colors.border : 'border-gray-200'}
            ${!isEarned && 'opacity-70'}
          `}
        >
          {/* Icon */}
          <div className={`relative z-10 ${isEarned ? colors.text : 'text-gray-400'}`}>
            <Icon style={{ width: config.icon, height: config.icon }} />
          </div>

          {/* Progress ring for unearned badges */}
          {!isEarned && progress > 0 && (
            <svg
              className="absolute inset-0 w-full h-full -rotate-90"
              viewBox="0 0 100 100"
            >
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke="#e2e8f0"
                strokeWidth="6"
              />
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke="currentColor"
                strokeWidth="6"
                strokeLinecap="round"
                strokeDasharray={`${progress * 2.83} 283`}
                className={colors.text}
              />
            </svg>
          )}

          {/* Shine effect for earned badges */}
          {isEarned && (
            <motion.div
              className="absolute inset-0 rounded-xl overflow-hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 0.3, 0] }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
            >
              <div
                className={`absolute inset-0 bg-gradient-to-r ${colors.gradient} opacity-20`}
                style={{
                  transform: 'translateX(-100%) skewX(-15deg)',
                  animation: 'shine 3s infinite',
                }}
              />
            </motion.div>
          )}
        </div>

        {/* Tooltip */}
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
          <div className="bg-dark-900 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap shadow-lg">
            <p className="font-semibold">{badge.name}</p>
            <p className="text-dark-300 text-[10px]">{badge.description}</p>
            {!isEarned && (
              <p className="text-primary-300 text-[10px] mt-1">
                {badge.progress}% complete
              </p>
            )}
          </div>
          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-dark-900" />
        </div>

        {/* Earned indicator */}
        {isEarned && (
          <motion.div
            className={`absolute -top-1 -right-1 w-3 h-3 rounded-full bg-gradient-to-r ${colors.gradient}`}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, type: 'spring' }}
          />
        )}
      </motion.div>
    </AnimatePresence>
  );
}
